import type { RefillMiddleware, RefillParams, RefillParamsModifier } from '../../boardMVC/models/tray/RefillMiddleware';
import { applyRefillParamsModifier } from '../../boardMVC/models/tray/RefillMiddleware';

import { macroBalancingConfig } from './macroBalancingConfig';
import type { MacroBalancingDependencies } from './types';

interface MacroBalancingRefillMiddlewareConfig {
  isDev: boolean;
  observable: MacroBalancingDependencies['observable'];
  getGoalProgress: () => number;
}

export class MacroBalancingRefillMiddleware implements RefillMiddleware {
  constructor(private readonly config: MacroBalancingRefillMiddlewareConfig) {}

  public processRefill(params: RefillParams): RefillParams {
    const modifier = this.getParamsModifier();
    if (!modifier) return params;
    return applyRefillParamsModifier(params, modifier);
  }

  private getParamsModifier(): RefillParamsModifier | null {
    const { getGoalProgress } = this.config;
    const abilityScore = this.getAbilityScore();

    this.log(`MacroBalancingRefillMiddleware ability score: ${abilityScore}`);

    const tierToUse = macroBalancingConfig.tiers.find(
      (tier) => tier.range.min <= abilityScore && tier.range.max >= abilityScore,
    );

    if (!tierToUse) {
      this.log(`MacroBalancingRefillMiddleware no modifier to use`);
      return null;
    }
    if (tierToUse.minGoalProgress > getGoalProgress()) {
      this.log(`MacroBalancingRefillMiddleware didn't meet goal requirements`);
      return null;
    }

    this.log(`MacroBalancingRefillMiddleware modifier`, tierToUse.modifier);
    return tierToUse.modifier;
  }

  private getAbilityScore(): number {
    const { macroBalancing } = this.config.observable.getValue();
    if (!macroBalancing) return 0;
    return Math.max(0, macroBalancing.abilityScore - (macroBalancing.attemptOverride ? 4 : 0));
  }

  private log(...logs: unknown[]): void {
    if (!this.config.isDev) return;
    console.warn(...logs);
  }
}
